import type { RemoteStoreInfoQuery } from '@shared/gql/document-nodes'
import type { Spacings } from '@sok/design-system'
import type { FC } from 'react'
import type { CSSObject } from 'styled-components'
import type { IStorePageContentFields } from 'types/contentful/generated'

import { Domain } from '@shared/gql/document-nodes'
import { NextSeo } from 'next-seo'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import { CONTENT_PAGE_MAX_WIDTH } from 'styles/layout'
import { useCanonicalUrl } from 'utils/hooks/use-canonical-url'
import { ContentPageLayout } from 'views/contentful/components/ContentPageLayout'

import { OnlineStoreContactInfo } from './components/OnlineStoreContactInfo'
import { StoreContactInfo } from './components/StoreContactInfo'
import { StoreLatestContent } from './components/StoreLatestContent'
import { StoreOnlineStore } from './components/StoreOnlineStore'
import { StoreOpeningTimes } from './components/StoreOpeningTimes/StoreOpeningTimes'
import { StoreServices } from './components/StoreServices'
import { StoresPageLink } from './components/StoresPageLink'

const spaceBetween = (spacing: Spacings[keyof Spacings]): CSSObject => ({
  ['> * + *']: {
    marginTop: spacing,
  },
})

const StyledPageContent = styled.div(({ theme }) => ({
  backgroundColor: theme.color.white,
  width: '100%',
  padding: `${theme.spacings.xLarge} ${theme.spacings.medium}`,
}))

const StyledPageContentContainer = styled.div(({ theme }) => ({
  width: '100%',
  maxWidth: CONTENT_PAGE_MAX_WIDTH,
  margin: '0 auto',

  ...spaceBetween(theme.spacings.xxLarge),
}))

const StyledStorePageContentWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  rowGap: theme.spacings.large,
  columnGap: theme.spacings.xxLarge,
}))

const StyledStorePageSidebar = styled.aside(({ theme }) => ({
  flexGrow: '1',
  flexBasis: '360px',

  ...spaceBetween(theme.spacings.xLarge),
}))

const StyledStorePageMainContent = styled.main(({ theme }) => ({
  flexBasis: 0,
  flexGrow: 999,
  minWidth: '50%',
  ...spaceBetween(theme.spacings.xLarge),
}))

const TitleContainer = styled.div(({ theme }) => ({
  ...spaceBetween(theme.spacings.xSmall),
}))

const Title = styled.h1(({ theme }) => theme.variants.title4)

export interface StorePageProps {
  store: NonNullable<RemoteStoreInfoQuery['store']>
  storeContent: IStorePageContentFields | null
}

export const StorePage: FC<StorePageProps> = ({ store, storeContent }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const seoCanonical = useCanonicalUrl()
  const seoTitle = `${store.name} | ${t('Store seo title')} | ${theme.seoTags.title}`
  const seoDescription = t('Store seo description', { storeName: store.name })

  return (
    <ContentPageLayout data-test-id="store-page">
      <NextSeo
        title={seoTitle}
        canonical={seoCanonical}
        description={seoDescription}
        openGraph={{
          url: seoCanonical,
          title: seoTitle,
          type: 'website',
          description: seoDescription,
        }}
      />

      <StyledPageContent>
        <StyledPageContentContainer>
          <TitleContainer>
            <StoresPageLink />
            <Title data-test-id="store-name">{store.name}</Title>
          </TitleContainer>

          <StyledStorePageContentWrapper>
            <StyledStorePageSidebar>
              <StoreOnlineStore
                showStoreLink={store.domains.includes(Domain.SKaupat)}
                store={store}
              />
              {store.domains.includes(Domain.SKaupat) && <OnlineStoreContactInfo />}
              <StoreContactInfo store={store} deliveryAreas={store} />
              <StoreOpeningTimes store={store} />
            </StyledStorePageSidebar>

            <StyledStorePageMainContent>
              {storeContent && <StoreLatestContent storeContent={storeContent} />}
              <StoreServices store={store} />
            </StyledStorePageMainContent>
          </StyledStorePageContentWrapper>
        </StyledPageContentContainer>
      </StyledPageContent>
    </ContentPageLayout>
  )
}
